import React, { useRef, useState } from "react";
import {  createRecord, ScanRecord } from "../../components/ResultTable";
import { useScanQueueConsumer } from "../../components/useScanQueueConsumer";
import { PdaScanEvent } from "../../components/Pda";
import { parseException, showError, showRepeatedScanError, parseExceptionMessageKey } from "../../../../utils/exceptionUtils";
import { ScanInput } from "../../components/ScanInput";
import { isValidBarCode, showBarCodeError, isBagNumber } from "../../../../utils/BarCodeUtil";
import { playScanFailedAoSound } from "../../../../utils/scanUtils";
import { t } from '../../../../i18n';
import { Toast, Tabs, Dialog, Button } from 'antd-mobile';
import { Table } from 'antd';
import { ApolloError } from '@apollo/client';

export interface HandleTmsLoadingScanEventResult {
    success: boolean;
    parcelQuantity?: number | null;
    message?: string | null;
    destinationSortingCode?: string | null;
}

type TmsLoadingScanComponentProps = {
    value1Placeholder: string;
    value1ColumnTitle: string;
    handleScanEvent: (barCode: string, forceSubmit?: boolean) => Promise<HandleTmsLoadingScanEventResult>;
    routeName?: string;
    /** 扫描成功后 Toast 显示的数量前缀，默认为装车 */
    quantityLabel?: string;
}

// 扩展 ScanRecord 以包含 destinationSortingCode
interface TmsLoadingScanRecord extends ScanRecord {
    destinationSortingCode?: string | null;
}

export const TmsLoadingScanComponent: React.FC<TmsLoadingScanComponentProps> = ({
    value1Placeholder,
    value1ColumnTitle,
    handleScanEvent,
    routeName,
    quantityLabel
}) => {
    const [successRecords, setSuccessRecords] = useState<TmsLoadingScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [value1, setValue1] = useState('');
    const [totalQuantity, setTotalQuantity] = useState<number>(0);
    const [activeTab, setActiveTab] = useState('success');

    // 扫描执行锁（参考 VehicleRelatedScanComponent）
    const executingRef = useRef<{
        isExecuting: boolean;
        lastBarCode: string;
        lastExecuteTime: number;
    }>({
        isExecuting: false,
        lastBarCode: '',
        lastExecuteTime: 0
    });

    // 用 ref 保存 handleScanEvent 和 routeName，以便在 onProcess 回调中拿到最新值
    const handleScanEventRef = useRef(handleScanEvent);
    handleScanEventRef.current = handleScanEvent;
    const routeNameRef = useRef(routeName);
    routeNameRef.current = routeName;

    const { enqueueScan } = useScanQueueConsumer({
        maxConcurrency: 1,
        onProcess: async (event: PdaScanEvent) => {
            const barCode = event.code;

            try {
                // 兼容收起键盘
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }

                const now = Date.now();
                const executing = executingRef.current;

                // 检查是否是重复扫描（3秒内的相同条码视为重复）
                if (
                    executing.lastBarCode === barCode &&
                    now - executing.lastExecuteTime < 3000
                ) {
                    console.log('Duplicate scan detected, ignoring...');
                    return;
                }

                // 如果当前有扫描正在执行，直接返回
                if (executing.isExecuting) {
                    console.log('Previous scan still executing, ignoring...');
                    return;
                }

                // 设置执行锁
                executing.isExecuting = true;
                executing.lastBarCode = barCode;
                executing.lastExecuteTime = now;

                console.log('TmsLoadingScanComponent handleScan', barCode);
                setValue1(barCode);

                let shouldReleaseLockInFinally = true;

                try {
                    const result = await handleScanEventRef.current(barCode);
                    console.warn(barCode + '-TmsLoadingScanComponent handleScan result:', result);

                    // 关键差异点：
                    // 后端 handleTmsLoadingScanEvent 在业务失败时可能"返回 success=false + message"，而不是抛异常。
                    // 如果这里不处理，会导致失败记录不入表（也就看不到失败数量/错误提示）。
                    if (!result.success) {
                        // 处理国际化：如果 message 看起来像 messageKey（包含 "errors."），则进行国际化翻译
                        let message = result.message || t('pda.common.submitFailed') || t('pda.common.failed') || 'Failed';
                        if (message && message.includes('errors.')) {
                            // 尝试作为 messageKey 进行国际化
                            const translatedMessage = t(message);
                            if (translatedMessage !== message) {
                                message = translatedMessage;
                            }
                        }
                        playScanFailedAoSound();
                        showError({ title: t('pda.common.error') || 'Error', message });
                        setFailedRecords(prev => [createRecord({ value1: barCode, failedReason: message }), ...prev]);
                        return;
                    }

                    const parcelQuantity = result.parcelQuantity || 0;
                    const destinationSortingCode = result.destinationSortingCode || null;
                    const newRecord: TmsLoadingScanRecord = {
                        ...createRecord({
                            value1: barCode,
                            value2: parcelQuantity > 0 ? parcelQuantity.toString() : '-'
                        }),
                        destinationSortingCode
                    };

                    setSuccessRecords(prev => [newRecord, ...prev]);
                    setTotalQuantity(prev => prev + parcelQuantity);

                    if (parcelQuantity > 0) {
                        Toast.show({
                            content: `${quantityLabel ?? t('pda.pages.shipOut.loading') ?? 'Loading'}${t('pda.common.quantity') || 'quantity'}: ${parcelQuantity} `,
                            position: 'center',
                            duration: 2000
                        });
                    }
                } catch (error) {
                    const messageKey = parseExceptionMessageKey(error);
                    let message: string;

                    // 特殊处理：当 messageKey 为 'errors.business.tms.loadingDetail.destinationNotInRoute' 时，显示确认框
                    // 其他新的错误消息（如 TMS_LOADING_NO_SUBSEQUENT_WAYPOINTS, TMS_LOADING_ORDER_NOT_FOUND 等）
                    // 会通过 parseException 自动处理国际化翻译，无需特殊处理
                    if (messageKey === 'errors.business.tms.loadingDetail.destinationNotInRoute') {
                        shouldReleaseLockInFinally = false;
                        // 从错误中提取 details（routeName 从 props 传入，不再从错误详情中获取）
                        let destinationSortingCode = '';
                        if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
                            const graphQLError = error.graphQLErrors[0];
                            const details = graphQLError.extensions?.details as any;
                            destinationSortingCode = details?.sortingCode || '';
                        }

                        // 显示确认框
                        return new Promise<void>((resolve) => {
                            let handled = false; // 标志位：防止重复处理
                            const handler = Dialog.show({
                                title: t('pda.pages.tms.loading.destinationNotInRouteTitle'),
                                content: (
                                    <div style={{ backgroundColor: '#ffffff', padding: '16px' }}>
                                        <div className="text-base text-gray-800 space-y-2">
                                            <div>
                                                <span className="font-semibold">{t('pda.pages.tms.loading.currentRoute')}:</span>
                                                <span className="ml-2 font-bold text-purple-600">{routeNameRef.current || '-'}</span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="font-semibold">{t('pda.pages.tms.loading.scannedBagPackage')}:</span>
                                                <span className="font-bold text-red-600">{barCode}</span>
                                            </div>
                                            <div>
                                                <span className="font-semibold">{t('pda.pages.tms.loading.destination')}:</span>
                                                <span className="ml-2 font-bold text-red-600">{destinationSortingCode || '-'}</span>
                                            </div>
                                            <div className="pt-8 mt-8 mb-4 pb-4 text-black">
                                                {t('pda.pages.tms.loading.destinationNotInRouteMessage')}
                                            </div>
                                            <div className="mt-6 flex gap-3">
                                                <Button
                                                    size="small"
                                                    block
                                                    onClick={() => {
                                                        if (handled) return;
                                                        handled = true;
                                                        handler.close();
                                                        // 点击取消，记录失败并释放锁
                                                        const { message: errorMessage } = parseException(error);
                                                        setFailedRecords(prev => [createRecord({ value1: barCode, failedReason: errorMessage }), ...prev]);
                                                        executing.isExecuting = false;
                                                        resolve();
                                                    }}
                                                    style={{ flex: 1, backgroundColor: '#f3f4f6', color: '#374151', borderColor: '#f3f4f6' }}
                                                >
                                                    {t('pda.common.cancel') || 'cancel'}
                                                </Button>
                                                <Button
                                                    size="small"
                                                    block
                                                    color="primary"
                                                    onClick={async () => {
                                                        if (handled) return;
                                                        handled = true;
                                                        handler.close();
                                                        // 点击继续后，重新发起接口请求，forceSubmit 设为 true
                                                        try {
                                                            const retryResult = await handleScanEventRef.current(barCode, true);
                                                            if (!retryResult.success) {
                                                                const retryMessage = retryResult.message || t('pda.common.scanFailed') || '扫描失败';
                                                                playScanFailedAoSound();
                                                                showError({ title: t('pda.common.error') || 'Error', message: retryMessage });
                                                                setFailedRecords(prev => [createRecord({ value1: barCode, failedReason: retryMessage }), ...prev]);
                                                            } else {
                                                                // 重试成功，正常处理结果
                                                                const parcelQuantity = retryResult.parcelQuantity || 0;
                                                                const destinationSortingCode = retryResult.destinationSortingCode || null;
                                                                const newRecord: TmsLoadingScanRecord = {
                                                                    ...createRecord({
                                                                        value1: barCode,
                                                                        value2: parcelQuantity > 0 ? parcelQuantity.toString() : '-'
                                                                    }),
                                                                    destinationSortingCode
                                                                };

                                                                setSuccessRecords(prev => [newRecord, ...prev]);
                                                                setTotalQuantity(prev => prev + parcelQuantity);

                                                                if (parcelQuantity > 0) {
                                                                    Toast.show({
                                                                        content: `${quantityLabel ?? t('pda.pages.shipOut.loading') ?? 'Loading'}${t('pda.common.quantity') || 'quantity'}: ${parcelQuantity} `,
                                                                        position: 'center',
                                                                        duration: 2000
                                                                    });
                                                                }
                                                            }
                                                        } catch (retryError) {
                                                            // 重试失败，显示错误提示
                                                            const { message: retryMessage } = parseException(retryError);
                                                            playScanFailedAoSound();
                                                            showError({ title: t('pda.common.error') || 'Error', message: retryMessage });
                                                            setFailedRecords(prev => [createRecord({ value1: barCode, failedReason: retryMessage }), ...prev]);
                                                        } finally {
                                                            executing.isExecuting = false;
                                                            resolve();
                                                        }
                                                    }}
                                                    style={{ flex: 1 }}
                                                >
                                                    {t('pda.common.continueButton') || 'continue'}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ),
                                closeOnAction: true,
                                onClose: () => {
                                    // 只有在未被处理过的情况下才执行（即用户点击背景或ESC键关闭弹窗时）
                                    if (!handled) {
                                        handled = true;
                                        const { message: errorMessage } = parseException(error);
                                        setFailedRecords(prev => [createRecord({ value1: barCode, failedReason: errorMessage }), ...prev]);
                                        executing.isExecuting = false;
                                    }
                                    resolve();
                                }
                            });
                        });
                    }

                    // 检查是否是自定义的扫描失败错误
                    if ((error as any)?.customMessage) {
                        message = (error as any).customMessage;
                    } else if (messageKey === "errors.business.alreadyScanned") {
                        showRepeatedScanError(error);
                        const { message: errorMessage } = parseException(error);
                        message = errorMessage;
                    } else {
                        const { title, message: errorMessage } = parseException(error);
                        playScanFailedAoSound();
                        showError({ title, message: errorMessage });
                        message = errorMessage;
                    }

                    setFailedRecords(prev => [createRecord({ value1: barCode, failedReason: message }), ...prev]);
                } finally {
                    // 确保执行锁一定会被释放（除非是确认框场景，那里会单独处理）
                    if (shouldReleaseLockInFinally) {
                        executing.isExecuting = false;
                    }
                }
            } catch (error) {
                // 外层异常兜底，确保执行锁被释放
                executingRef.current.isExecuting = false;
            }
        }
    });

    const handleScan = (code: string) => {
        if (isValidBarCode(code)) {
            enqueueScan(code);
        } else {
            showBarCodeError(code);
        }
    };

    const failedColumns = [
        { title: value1ColumnTitle, key: 'value1' },
        { title: t('pda.common.time'), key: 'time' },
        {title: t('pda.common.failedReason'), key: 'failedReason'}
    ];

    const successCount = successRecords.length;
    const failedCount = failedRecords.length;

    // 渲染成功卡片
    const renderSuccessCard = (record: TmsLoadingScanRecord) => {
        const barCode = record.value1 || '';
        const isBag = isBagNumber(barCode);
        const bgColor = isBag ? 'bg-purple-500' : 'bg-orange-400';
        const destinationSortingCode = record.destinationSortingCode || '-';
        const parcelQuantity = record.value2 || '-';

        return (
            <div key={record.id} className="bg-white rounded-lg p-3 mb-2 shadow-sm">
                {/* 第一行：扫描号码（背景色和文字大小相等） */}
                <div className="mb-2">
                    <span className={`${bgColor} text-white px-2 py-0.5 rounded text-xs font-medium inline-block`}>
                        {barCode}
                    </span>
                </div>
                {/* 第二行：标签 */}
                <div className="flex justify-between text-sm font-semibold text-gray-700 mb-1">
                    <span>{t('pda.pages.tms.loading.destinationCenter') || '目的中心'}</span>
                    <span>{t('pda.pages.tms.loading.waybillQuantity') || '运单数量'}</span>
                </div>
                {/* 第三行：数值 */}
                <div className="flex justify-between text-base font-bold">
                    <span>{destinationSortingCode}</span>
                    <span>{parcelQuantity}</span>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-2">
            <ScanInput placeholder={value1Placeholder} value={value1} onScan={handleScan}/>

            {/* 选项卡 */}
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                className="border-b"
            >
                <Tabs.Tab title={`${t('pda.common.succeed')}(${successCount})`} key="success"/>
                <Tabs.Tab title={`${t('pda.common.failed')}(${failedCount})`} key="failed"/>
            </Tabs>

            {/* 数据列表 */}
            <div className="flex-1 overflow-auto">
                {activeTab === 'success' ? (
                    successRecords.length > 0 ? (
                        <div className="space-y-2">
                            {successRecords.map(record => renderSuccessCard(record))}
                        </div>
                    ) : (
                        <div className="p-4 text-center text-gray-500">{t('pda.common.noRecords')}</div>
                    )
                ) : (
                    failedRecords.length > 0 ? (
                        <div className="mt-2">
                            <Table
                                columns={failedColumns.map(column => ({
                                    title: column.title,
                                    dataIndex: column.key,
                                    key: column.key,
                                    onHeaderCell: () => ({
                                        className: 'whitespace-normal break-words'
                                    }),
                                    render: (text: string) => (
                                        <div className="whitespace-normal break-words break-all">
                                            {text}
                                        </div>
                                    ),
                                }))}
                                dataSource={failedRecords.map(record => ({
                                    key: record.id,
                                    ...record,
                                }))}
                                size="small"
                            />
                        </div>
                    ) : (
                        <div className="p-4 text-center text-gray-500">{t('pda.common.noRecords')}</div>
                    )
                )}
            </div>
        </div>
    );
};
